/**
 * App-hosted email-first login (EPT-3940).
 *
 * Unauthenticated entry goes to `/login` instead of Auth0 Universal Login.
 * The page collects the work email once, then Auth0 receives `login_hint` so
 * its identifier screen is skipped.
 *
 * This is the single entry point for "send the user to sign in". It carries
 * the same mode guards as `AppContext.loginWithRedirect` (Salesforce Canvas,
 * localhost) so callers cannot bypass them by navigating directly.
 */

import { isSalesforceEmbedded, usesEmulatorAuth } from '../config';
import { isLocalhost } from './localAuth';

export const APP_LOGIN_PATH = '/login';
/** Where logout lands (EPD-79). Public, and never a place to send users back to. */
export const SIGNED_OUT_PATH = '/signed-out';

/** Shown by deep-link pages when `redirectToAppLogin` declined to navigate. */
export const LOGIN_UNAVAILABLE_MESSAGE =
  'Sign-in is not available in this view. Open this link in a browser tab to continue.';

const REDIRECT_PATH_KEY = 'redirectPath';

// Set when logout starts navigating to the IdP's logout endpoint. Nothing may
// replace that navigation: a later assign('/login') cancels it and leaves the
// IdP session alive, so /login signs the user straight back in. The page is
// unloading, so this never needs resetting.
let leavingForLogout = false;

/** Logout is navigating away; from here on, nothing else may navigate. */
export function beginLogoutNavigation(): void {
  leavingForLogout = true;
}

export function isLeavingForLogout(): boolean {
  return leavingForLogout;
}

/**
 * True when `path` is a sign-in screen (`/login` or `/signed-out`), with or
 * without query/hash. Callers use it to never auto-redirect away from, or back
 * to, these pages.
 *
 * Example: `/login` → true; `/signed-out` → true; `/room/1` → false.
 */
export function isAppLoginPath(path: string | null | undefined): boolean {
  if (!path) {
    return false;
  }
  const pathname = path.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  return pathname === APP_LOGIN_PATH || pathname === SIGNED_OUT_PATH;
}

/**
 * Persist a same-origin return path and send the browser to `/login`.
 *
 * Returns whether it actually navigated, so a caller that latches a
 * "redirecting..." state can unlatch when the redirect was suppressed.
 *
 * Example: `redirectToAppLogin('/room/42')` stores `/room/42`, assigns `/login`, returns true.
 * Example: already on `/login` → false (avoids a redirect loop).
 * Example: logout already navigating to the IdP → false (would cancel it).
 * Example: embedded in Salesforce Canvas → false (never navigates the iframe).
 */
export function redirectToAppLogin(returnTo?: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  if (leavingForLogout) {
    return false;
  }
  if (isAppLoginPath(window.location.pathname)) {
    return false;
  }
  // Emulator auth on localhost: AppAuthProvider fetches `/local-token` on mount.
  // Sending dev to /login would make them fill in a form for a free token.
  //
  // Require both the real loopback hostname AND emulator auth so that:
  // - `?env=LOCAL` on a hosted app still redirects to /login (recovery path)
  // - `VITE_REMOTE_BACKEND=PROD` on localhost still redirects to Auth0 login
  // Order matches AppContext, where `isSalesforceCanvas` is
  // `!isLocal && isSalesforceEmbedded()`.
  if (isLocalhost() && usesEmulatorAuth()) {
    return false;
  }
  // Salesforce Canvas: the app runs in an iframe Auth0 refuses to render in
  // (frame-ancestors), so navigating to /login would strand the user on a form
  // whose submit can never complete. AppContext.loginWithRedirect has always
  // no-opped here; this keeps that true for callers that bypass it. Callers
  // must surface LOGIN_UNAVAILABLE_MESSAGE rather than wait forever.
  if (isSalesforceEmbedded()) {
    console.warn('[SALESFORCE CANVAS] Ignoring hosted login redirect in embedded mode');
    return false;
  }

  const candidate =
    returnTo ?? `${window.location.pathname}${window.location.search}`;
  if (candidate.startsWith('/') && !candidate.startsWith('//') && !isAppLoginPath(candidate)) {
    localStorage.setItem(REDIRECT_PATH_KEY, candidate);
  }
  window.location.assign(APP_LOGIN_PATH);
  return true;
}
